// Project Admin — Cost Centers & Cost Categories (PDF page 16)
import React, { useEffect, useMemo, useState } from 'react';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Stat, Tabs, Field, Input, Select, Badge, Note, Modal, Empty, Loading, Spin, ReqStatus,
  useAsync, useAction, useDirectory, useProjects, clearCache, pad2, fmtDate,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

const LEVELS = ['Company', 'Business unit', 'Division', 'Site'];

function Tree({ nodes, parentId, selected, onSelect, expanded, toggle }) {
  const kids = nodes.filter((n) => String(n.parentId || '') === String(parentId || ''));
  if (!kids.length) return null;
  return (
    <ul>
      {kids.map((n) => {
        const hasKids = nodes.some((x) => String(x.parentId) === String(n._id));
        const open = expanded[n._id] !== false;
        return (
          <li key={n._id}>
            <div className={`node-row ${selected === n._id ? 'selected' : ''}`} onClick={() => onSelect(n._id)}>
              <span className="toggle" onClick={(e) => { e.stopPropagation(); if (hasKids) toggle(n._id); }}>{hasKids ? <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`} /> : null}</span>
              <span className="circle" />
              <span style={{ opacity: n.status === 'Active' ? 1 : 0.5 }}>{n.name}</span>
            </div>
            {hasKids && open && <Tree nodes={nodes} parentId={n._id} selected={selected} onSelect={onSelect} expanded={expanded} toggle={toggle} />}
          </li>
        );
      })}
    </ul>
  );
}

export default function CostCenters() {
  const { user } = useAuth();
  const canEdit = ['admin', 'finance'].includes(user?.role);
  const people = useDirectory();
  const projects = useProjects();
  const [tab, setTab] = useState('centers');
  const centers = useAsync(() => wsAPI.costCenters(), []);
  const cats = useAsync(() => wsAPI.costCategories(), []);
  const stats = useAsync(() => wsAPI.costCenterStats(), []);
  const mappings = useAsync(() => wsAPI.projectMappings(), []);
  const mapReqs = useAsync(() => wsAPI.listRequests({ type: 'cost_mapping', scope: 'all' }), []);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [ccModal, setCcModal] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [pmModal, setPmModal] = useState(null);
  const [tx, setTx] = useState({ employeeId: '', project: '', costCenter: '', costCategory: '' });
  const [detail, setDetail] = useState(null);
  const [busy, run] = useAction();

  const nodes = centers.data?.costCenters || [];
  const categories = cats.data?.costCategories || [];
  useEffect(() => {
    if (!selected && nodes.length) setSelected((nodes.find((n) => n.level === 'Site') || nodes[0])._id);
  }, [nodes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const sel = nodes.find((n) => n._id === selected);
  const selCats = useMemo(() => (sel?.categories?.length ? categories.filter((c) => sel.categories.includes(c.code)) : categories), [sel, categories]);
  const reloadAll = () => { centers.reload(); cats.reload(); stats.reload(); mappings.reload(); clearCache('costCenters'); clearCache('costCategories'); };

  const saveCc = async () => {
    if (!ccModal.code || !ccModal.name) { notify.error('Code and name are required'); return; }
    const r = await run('cc', () => (ccModal._id ? wsAPI.updateCostCenter(ccModal._id, ccModal) : wsAPI.createCostCenter(ccModal)));
    if (r) { setCcModal(null); reloadAll(); if (!ccModal._id) setSelected(r.costCenter._id); }
  };
  const saveCat = async () => {
    if (!catModal.code || !catModal.name) { notify.error('Code and name are required'); return; }
    const r = await run('cat', () => (catModal._id ? wsAPI.updateCostCategory(catModal._id, catModal) : wsAPI.createCostCategory(catModal)));
    if (r) { setCatModal(null); reloadAll(); }
  };
  const toggleCat = async (code, on) => {
    const current = sel.categories?.length ? sel.categories : categories.map((c) => c.code);
    const next = on ? [...new Set([...current, code])] : current.filter((c) => c !== code);
    const r = await run('toggle', () => wsAPI.updateCostCenter(sel._id, { categories: next }), false);
    if (r) centers.reload();
  };
  const removeCc = async () => {
    const r = await run('del', () => wsAPI.deleteCostCenter(sel._id), 'Cost center deleted');
    if (r) { setSelected(null); reloadAll(); }
  };
  const submitMapping = async () => {
    const miss = [['employeeId', 'Employee'], ['project', 'Project'], ['costCenter', 'Cost center'], ['costCategory', 'Cost category']].filter(([k]) => !tx[k]).map(([, l]) => l);
    if (miss.length) { notify.error(`Finance validation: select ${miss.join(', ')}`); return; }
    const emp = people.find((u) => u._id === tx.employeeId);
    const r = await run('tx', () => wsAPI.createRequest({ type: 'cost_mapping', title: `${emp?.userName} → ${tx.project} → ${tx.costCenter} → ${tx.costCategory}`, project: tx.project, costCenter: tx.costCenter, costCategory: tx.costCategory, data: { ...tx, employeeName: emp?.userName }, submit: true }), 'Mapping submitted for Finance validation');
    if (r) { setTx({ employeeId: '', project: '', costCenter: '', costCategory: '' }); mapReqs.reload(); }
  };
  const s = stats.data?.stats || {};

  return (
    <PageShell section="Project Admin">
      <PageHead eyebrow="Project Admin" title="Cost Centers & Cost Categories"
        actions={(
          <>
            {canEdit && <button type="button" className="tse-btn lg outline" onClick={() => setCcModal({ code: '', name: '', level: 'Site', parentId: selected || '', owner: 'Finance', status: 'Active' })}><i className="bi bi-plus-lg" />Add cost center</button>}
            {canEdit && <button type="button" className="tse-btn lg outline" onClick={() => setCatModal({ code: '', name: '', accountCode: '', status: 'Active' })}><i className="bi bi-plus-lg" />Add category</button>}
            <button type="button" className="tse-btn lg" onClick={() => document.getElementById('tx-mapping')?.scrollIntoView({ behavior: 'smooth' })}><i className="bi bi-send-fill" />Submit mapping</button>
          </>
        )} />
      <div className="mb-18" style={{ marginTop: -8 }}><Tabs value={tab} onChange={setTab} tabs={[{ key: 'centers', label: 'Cost centers' }, { key: 'categories', label: 'Cost categories' }, { key: 'mappings', label: 'Project mappings' }]} /></div>

      <div className="tse-grid g-3 mb-18">
        <Stat icon="bi-diagram-3" tone="blue" label="Active cost centers" value={pad2(s.activeCostCenters)} loading={stats.loading} onClick={() => setTab('centers')} />
        <Stat icon="bi-file-earmark-text" tone="green" label="Cost categories" value={pad2(s.costCategories)} loading={stats.loading} onClick={() => setTab('categories')} />
        <Stat icon="bi-exclamation-triangle" tone="red" label="Unmapped requests" value={pad2(s.unmappedRequests)} loading={stats.loading} />
      </div>

      {tab === 'centers' && (
        <div className="tse-split half mb-18" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
          <Card title="Cost center hierarchy">
            {centers.loading ? <Loading /> : !nodes.length ? <Empty icon="bi-diagram-3" title="No cost centers yet" sub={canEdit ? 'Add the company, business units, divisions and sites.' : 'Finance maintains the hierarchy.'} /> : (
              <div className="tse-row" style={{ alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <div className="tse-tree" style={{ flex: 1, minWidth: 240 }}><Tree nodes={nodes} parentId={null} selected={selected} onSelect={setSelected} expanded={expanded} toggle={(id) => setExpanded((e) => ({ ...e, [id]: e[id] === false }))} /></div>
                {sel && (
                  <div style={{ background: '#f4f7fa', border: '1px solid #e1e9f1', borderRadius: 8, padding: '10px 13px', minWidth: 230 }}>
                    <div className="tse-small tse-muted">Selected cost center</div>
                    <div style={{ fontSize: '1.014rem', fontWeight: 700, margin: '3px 0 7px' }}>{sel.code}</div>
                    <div className="tse-row" style={{ gap: 8 }}>
                      <div><div className="tse-small tse-muted">Owner</div><div className="tse-strong">{sel.owner}</div></div>
                      <div><div className="tse-small tse-muted">Status</div><Badge tone={sel.status === 'Active' ? 'approved' : 'gray'}>{sel.status}</Badge></div>
                    </div>
                    <div className="tse-small tse-muted" style={{ marginTop: 8 }}>{sel.level} · {sel.name}</div>
                    {canEdit && (
                      <div className="tse-row" style={{ marginTop: 8, gap: 8 }}>
                        <button type="button" className="tse-btn xs outline" onClick={() => setCcModal({ ...sel, parentId: sel.parentId || '' })}>Edit</button>
                        <button type="button" className="tse-btn xs outline" onClick={() => setCcModal({ code: `${sel.code}-`, name: '', level: LEVELS[Math.min(3, LEVELS.indexOf(sel.level) + 1)], parentId: sel._id, owner: sel.owner, status: 'Active' })}>Add child</button>
                        <button type="button" className="tse-btn xs danger-outline" onClick={removeCc} disabled={busy === 'del'}>Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
          <Card title={`Cost categories for ${sel?.code || '—'}`} bodyClass="flush">
            {!sel ? <Empty title="Select a cost center" /> : (
              <div className="tse-table-wrap" style={{ padding: '0 11px 11px' }}><table className="tse-table bordered">
                <thead><tr><th>Code</th><th>Category</th><th>Account mapping</th>{canEdit && <th style={{ width: 90 }}>Enabled</th>}</tr></thead>
                <tbody>{(canEdit ? categories : selCats).map((c) => {
                  const enabled = !sel.categories?.length || sel.categories.includes(c.code);
                  return (
                    <tr key={c._id} style={{ opacity: enabled ? 1 : 0.55 }}>
                      <td>{c.code}</td><td>{c.name}</td>
                      <td>{c.accountCode ? <Badge tone="approved">GL {c.accountCode}</Badge> : <Badge tone="pending">To be mapped</Badge>}</td>
                      {canEdit && <td><input type="checkbox" checked={enabled} style={{ width: 18, height: 18, accentColor: '#1479bd' }} onChange={(e) => toggleCat(c.code, e.target.checked)} /></td>}
                    </tr>
                  );
                })}</tbody>
              </table></div>
            )}
          </Card>
        </div>
      )}

      {tab === 'categories' && (
        <Card bodyClass="flush" className="mb-18">
          {cats.loading ? <Loading /> : (
            <div className="tse-table-wrap"><table className="tse-table">
              <thead><tr><th>Code</th><th>Category</th><th>Account (GL) mapping</th><th>Status</th><th>Used by cost centers</th>{canEdit && <th />}</tr></thead>
              <tbody>{categories.map((c) => (
                <tr key={c._id}>
                  <td className="strong">{c.code}</td><td>{c.name}</td>
                  <td>{c.accountCode ? <Badge tone="approved">GL {c.accountCode}</Badge> : <Badge tone="pending">To be mapped</Badge>}</td>
                  <td><Badge tone={c.status === 'Active' ? 'approved' : 'gray'}>{c.status}</Badge></td>
                  <td>{nodes.filter((n) => !n.categories?.length || n.categories.includes(c.code)).length}</td>
                  {canEdit && <td><button type="button" className="tse-btn xs outline" onClick={() => setCatModal({ ...c })}>Edit</button></td>}
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      )}

      {tab === 'mappings' && (
        <Card bodyClass="flush" className="mb-18">
          <div className="tse-table-wrap"><table className="tse-table">
            <thead><tr><th>Project</th><th>Name</th><th>Cost centers</th><th>Categories</th>{canEdit && <th />}</tr></thead>
            <tbody>{projects.map((p) => {
              const m = (mappings.data?.mappings || []).find((x) => x.projectCode === p.projectCode);
              return (
                <tr key={p._id}>
                  <td className="strong">{p.projectCode}</td><td>{p.name}</td>
                  <td>{(m?.costCenters || []).filter(Boolean).map((c) => <Badge key={c} tone="blue">{c}</Badge>)}{!(m?.costCenters || []).length && <span className="tse-muted">Not mapped</span>}</td>
                  <td>{(m?.categories || []).filter(Boolean).join(', ') || '—'}</td>
                  {canEdit && <td><button type="button" className="tse-btn xs outline" onClick={() => setPmModal({ code: p.projectCode, costCenters: m?.costCenters || [], categories: m?.categories || [] })}>Edit</button></td>}
                </tr>
              );
            })}</tbody>
          </table></div>
        </Card>
      )}

      <div id="tx-mapping">
        <Card title={<span>Transaction mapping <span style={{ fontWeight: 400 }}>(employee → project → cost center → category)</span></span>}>
          <div className="tse-row" style={{ alignItems: 'flex-end', flexWrap: 'nowrap', gap: 8 }}>
            <Field label="Employee" style={{ flex: 1 }}><Select value={tx.employeeId} onChange={(e) => setTx({ ...tx, employeeId: e.target.value })} placeholder="Select employee" options={people.map((u) => ({ value: u._id, label: u.userName }))} /></Field>
            <i className="bi bi-arrow-right" style={{ paddingBottom: 8 }} />
            <Field label="Project" style={{ flex: 1 }}><Select value={tx.project} onChange={(e) => setTx({ ...tx, project: e.target.value })} placeholder="Select project" options={projects.map((p) => p.projectCode)} /></Field>
            <i className="bi bi-arrow-right" style={{ paddingBottom: 8 }} />
            <Field label="Cost center" style={{ flex: 1 }}><Select value={tx.costCenter} onChange={(e) => setTx({ ...tx, costCenter: e.target.value })} placeholder="Select cost center" options={nodes.filter((n) => n.status === 'Active').map((n) => n.code)} /></Field>
            <i className="bi bi-arrow-right" style={{ paddingBottom: 8 }} />
            <Field label="Cost category" style={{ flex: 1 }}><Select value={tx.costCategory} onChange={(e) => setTx({ ...tx, costCategory: e.target.value })} placeholder="Select cost category" options={categories.filter((c) => c.status === 'Active').map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))} /></Field>
            <button type="button" className="tse-btn" onClick={submitMapping} disabled={busy === 'tx'}>{busy === 'tx' ? <Spin /> : <i className="bi bi-send" />}Submit</button>
          </div>
          <Note tone="info" style={{ marginTop: 8 }} title="Finance validation: Required fields prevent unallocated spend.">Please ensure employee, project, cost center and cost category are selected before submission.</Note>
          {(mapReqs.data?.requests || []).length > 0 && (
            <div className="tse-table-wrap" style={{ marginTop: 8 }}><table className="tse-table compact">
              <thead><tr><th>Ref</th><th>Mapping</th><th>Submitted by</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>{mapReqs.data.requests.slice(0, 8).map((r) => (
                <tr key={r._id} className="clickable" onClick={() => setDetail(r._id)}><td>{r.refNo}</td><td>{r.title}</td><td>{r.requesterName}</td><td><ReqStatus request={r} /></td><td>{fmtDate(r.createdAt)}</td></tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      </div>

      <Modal open={!!ccModal} onClose={() => setCcModal(null)} title={ccModal?._id ? 'Edit cost center' : 'Add cost center'}
        footer={<><button type="button" className="tse-btn gray" onClick={() => setCcModal(null)}>Cancel</button><button type="button" className="tse-btn" onClick={saveCc} disabled={busy === 'cc'}>{busy === 'cc' ? <Spin /> : null}Save</button></>}>
        {ccModal && (
          <div className="tse-form-grid c2">
            <Field label="Code" required><Input value={ccModal.code} onChange={(e) => setCcModal({ ...ccModal, code: e.target.value.toUpperCase() })} disabled={!!ccModal._id} placeholder="CC-WBU-EC-JSR" /></Field>
            <Field label="Name" required><Input value={ccModal.name} onChange={(e) => setCcModal({ ...ccModal, name: e.target.value })} /></Field>
            <Field label="Level"><Select value={ccModal.level} onChange={(e) => setCcModal({ ...ccModal, level: e.target.value })} options={LEVELS} /></Field>
            <Field label="Parent"><Select value={ccModal.parentId} onChange={(e) => setCcModal({ ...ccModal, parentId: e.target.value })} placeholder="— none (top level)" options={nodes.filter((n) => n._id !== ccModal._id).map((n) => ({ value: n._id, label: `${n.code} — ${n.name}` }))} /></Field>
            <Field label="Owner"><Select value={ccModal.owner} onChange={(e) => setCcModal({ ...ccModal, owner: e.target.value })} options={['Finance', 'Business Head', 'Project Manager', 'Administration']} /></Field>
            <Field label="Status"><Select value={ccModal.status} onChange={(e) => setCcModal({ ...ccModal, status: e.target.value })} options={['Active', 'Inactive']} /></Field>
          </div>
        )}
      </Modal>
      <Modal open={!!catModal} onClose={() => setCatModal(null)} title={catModal?._id ? 'Edit cost category' : 'Add cost category'}
        footer={<><button type="button" className="tse-btn gray" onClick={() => setCatModal(null)}>Cancel</button><button type="button" className="tse-btn" onClick={saveCat} disabled={busy === 'cat'}>{busy === 'cat' ? <Spin /> : null}Save</button></>}>
        {catModal && (
          <div className="tse-form-grid c2">
            <Field label="Code" required><Input value={catModal.code} maxLength={6} onChange={(e) => setCatModal({ ...catModal, code: e.target.value.toUpperCase() })} disabled={!!catModal._id} /></Field>
            <Field label="Category" required><Input value={catModal.name} onChange={(e) => setCatModal({ ...catModal, name: e.target.value })} /></Field>
            <Field label="Account (GL) code" hint="Leave blank if not yet mapped"><Input value={catModal.accountCode || ''} onChange={(e) => setCatModal({ ...catModal, accountCode: e.target.value })} /></Field>
            <Field label="Status"><Select value={catModal.status} onChange={(e) => setCatModal({ ...catModal, status: e.target.value })} options={['Active', 'Inactive']} /></Field>
          </div>
        )}
      </Modal>
      <Modal open={!!pmModal} onClose={() => setPmModal(null)} title={`Project mapping — ${pmModal?.code}`}
        footer={<><button type="button" className="tse-btn gray" onClick={() => setPmModal(null)}>Cancel</button><button type="button" className="tse-btn" onClick={async () => { const r = await run('pm', () => wsAPI.saveProjectMapping(pmModal.code, pmModal), 'Mapping saved'); if (r) { setPmModal(null); mappings.reload(); } }}>Save</button></>}>
        {pmModal && (
          <div className="tse-form-grid c2">
            <Field label="Cost centers">{nodes.map((n) => <label key={n._id} className="tse-check"><input type="checkbox" checked={pmModal.costCenters.includes(n.code)} onChange={(e) => setPmModal({ ...pmModal, costCenters: e.target.checked ? [...pmModal.costCenters, n.code] : pmModal.costCenters.filter((c) => c !== n.code) })} />{n.code}</label>)}</Field>
            <Field label="Categories">{categories.map((c) => <label key={c._id} className="tse-check"><input type="checkbox" checked={pmModal.categories.includes(c.code)} onChange={(e) => setPmModal({ ...pmModal, categories: e.target.checked ? [...pmModal.categories, c.code] : pmModal.categories.filter((x) => x !== c.code) })} />{c.code} — {c.name}</label>)}</Field>
          </div>
        )}
      </Modal>
      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={() => { mapReqs.reload(); stats.reload(); }} />
    </PageShell>
  );
}
